import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { AuthConfig } from 'angular-oauth2-oidc';

import { Router } from '@angular/router';
import { keycloakAuthConfig } from '../config/keycloak-auth.config';
import { ConfigService } from 'src/app/core/services/config.service';
import { BrandingConfig } from 'src/app/core/models/branding-config.model';
import { Es01ClientGlobalStore } from 'src/app/clients/es01/store/es01-client-global.store';

export interface KeycloakDynamicConfig {
  realm: string;
  clientId: string;
}

const BASE_CLIENT_ID = 'my-app';

const realmNameToClientId = (realmName: string): string => {
  const parts = realmName.split('-');
  const environment = parts.length > 1 ? parts[parts.length - 1] : null;

  return environment ? `${BASE_CLIENT_ID}-${environment}` : BASE_CLIENT_ID;
};

@Injectable({ providedIn: 'root' })
export class KeycloakDynamicConfigService {
  private readonly configService = inject(ConfigService);
  private readonly router = inject(Router);
  private readonly es01ClientStore = inject(Es01ClientGlobalStore);

  private configSubject = new BehaviorSubject<KeycloakDynamicConfig | null>(
    null
  );
  private configCache$: Observable<KeycloakDynamicConfig> | null = null;

  /**
   * Fetch Keycloak configuration from branding service
   * This will typically be called with a brand code to get the specific realm and client
   */
  fetchKeycloakConfig(): Observable<KeycloakDynamicConfig> {
    if (this.configCache$) {
      console.log('🔄 Using cached Keycloak configuration');
      return this.configCache$;
    }

    // Brand code is resolved from the es-01 client collection (full-host match),
    // loaded in APP_INITIALIZER before this runs. The realm/clientId derivation
    // below (via branding) is unchanged.
    const brandCode = this.es01ClientStore.currentBrandCode();
    console.log('🔍 Fetching Keycloak config for brand code:', brandCode);

    this.configCache$ = this.configService
      .getTenantConfiguration(brandCode)
      .pipe(
        map((brandConfig: BrandingConfig) => {
          console.log('✅ Successfully fetched brand config:', brandConfig);

          // Extract Keycloak configuration from brand config
          // Only realm and clientId come from branding service, domain is static from environment
          const keycloakConfig: KeycloakDynamicConfig = {
            realm: brandConfig.realm,
            clientId: realmNameToClientId(brandConfig.realm),
          };

          console.log('🔧 Extracted Keycloak config:', keycloakConfig);

          // Cache the config
          this.configSubject.next(keycloakConfig);

          return keycloakConfig;
        }),
        catchError(error => {
          console.error(
            '❌ Failed to fetch Keycloak configuration from branding service:',
            error
          );
          console.error('📡 Brand code used:', brandCode);
          console.error(
            '🔗 API endpoint:',
            `${environment.tenantApi}tenant/branding/brand-code/${brandCode}`
          );

          // Store error for post-bootstrap navigation
          sessionStorage.setItem(
            'keycloak-config-error',
            JSON.stringify({
              error:
                'Failed to fetch Keycloak configuration from branding service',
              timestamp: new Date().toISOString(),
              brandCode: brandCode,
              endpoint: `${environment.tenantApi}tenant/branding/brand-code/${brandCode}`,
              originalError: error.message || error,
            })
          );

          // Try to navigate to error page (may not work during APP_INITIALIZER)
          console.error('🔧 Attempting to redirect to public error page...');
          setTimeout(() => {
            this.router.navigate(['/config-error']).catch(() => {
              // Navigation failed, probably because router isn't ready
              console.log(
                'Router navigation failed, error stored in sessionStorage'
              );
            });
          }, 100);

          return throwError(error);
        }),
        shareReplay(1)
      );

    return this.configCache$;
  }

  /**
   * Create AuthConfig for angular-oauth2-oidc based on dynamic configuration
   * Uses static domain from environment + dynamic realm and clientId from branding service
   */
  createAuthConfig(dynamicConfig: KeycloakDynamicConfig): AuthConfig {
    return keycloakAuthConfig(dynamicConfig.realm, dynamicConfig.clientId);
  }

  /**
   * Get current cached configuration
   */
  getCurrentConfig(): KeycloakDynamicConfig | null {
    return this.configSubject.value;
  }

  /**
   * Observable for configuration changes
   */
  getConfigObservable(): Observable<KeycloakDynamicConfig | null> {
    return this.configSubject.asObservable();
  }

  /**
   * Clear cache to force reload on next request
   */
  clearCache(): void {
    this.configCache$ = null;
    this.configSubject.next(null);
  }
}
